import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Clock, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = ["Below ₹50,000", "₹50,000–₹1,00,000", "₹1,00,000–₹5,00,000", "₹5,00,000+"];
const YEARS = ["Less than 1 year", "1–3 years", "3–5 years", "5–10 years", "10+ years"];

const emptyForm = {
  name: "",
  phone: "",
  email: "",
  monthly_budget: "",
  city: "",
  industry: "",
  years_in_business: "",
};

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder-slate-400 transition-all duration-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100";

const Field = ({ label, id, error, children }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-slate-800">
      {label} <span className="text-brand-600">*</span>
    </label>
    {children}
    {error && (
      <p className="mt-1.5 text-xs font-medium text-red-600" data-testid={`${id}-error`}>
        {error}
      </p>
    )}
  </div>
);

const validate = (f) => {
  const errs = {};
  if (!f.name.trim() || f.name.trim().length < 2) errs.name = "Please enter your name";
  const digits = f.phone.replace(/[\s\-+]/g, "").replace(/^91(?=\d{10}$)/, "");
  if (!/^\d{10}$/.test(digits)) errs.phone = "Enter a valid 10-digit phone number";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) errs.email = "Enter a valid email address";
  if (!f.monthly_budget) errs.monthly_budget = "Select your monthly ads budget";
  if (!f.city.trim()) errs.city = "Please enter your city";
  if (!f.industry.trim()) errs.industry = "Please enter your industry";
  if (!f.years_in_business) errs.years_in_business = "Select years in business";
  return errs;
};

export default function Contact() {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      setForm(emptyForm);
      toast.success("Thanks! We'll contact you shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact Chatbudds — Get a Free Strategy Call"
        description="Tell us about your business and ad budget. The Chatbudds team will get back to you within 24 hours with a free strategy call."
      />

      <section className="relative overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 dot-grid" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-12 md:pt-44 md:pb-16">
          <Reveal>
            <span className="inline-flex items-center rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-700" data-testid="contact-eyebrow">
              Contact Us
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.06]">
              Let's grow your <span className="text-brand-600">business</span>.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-slate-600">
              Fill in the form and our team will get back to you within 24 hours.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 md:pb-28">
        <div className="grid gap-8 lg:grid-cols-[1.5fr_1fr]">
          <Reveal>
            <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm" data-testid="contact-form-card">
              {submitted ? (
                <div className="flex flex-col items-start gap-4 py-10" data-testid="contact-success-message">
                  <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                    <CheckCircle2 className="h-7 w-7" />
                  </span>
                  <h2 className="font-display text-2xl font-bold text-slate-900">Thanks! We'll contact you shortly.</h2>
                  <p className="text-sm leading-relaxed text-slate-600">
                    Your request has been received. Our team will reach out within 24 hours to schedule your free
                    strategy call.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    data-testid="contact-submit-another-button"
                    className="mt-2 inline-flex items-center gap-2 rounded-full border border-brand-200 px-6 py-3 text-sm font-semibold text-brand-700 transition-colors duration-300 hover:bg-brand-50 active:scale-95"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate data-testid="contact-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <Field label="Name" id="contact-name" error={errors.name}>
                      <input
                        id="contact-name"
                        type="text"
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Your full name"
                        className={inputClass}
                        data-testid="contact-name-input"
                      />
                    </Field>
                    <Field label="Phone Number" id="contact-phone" error={errors.phone}>
                      <input
                        id="contact-phone"
                        type="tel"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="10-digit mobile number"
                        className={inputClass}
                        data-testid="contact-phone-input"
                      />
                    </Field>
                    <Field label="Email ID" id="contact-email" error={errors.email}>
                      <input
                        id="contact-email"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@company.com"
                        className={inputClass}
                        data-testid="contact-email-input"
                      />
                    </Field>
                    <Field label="Monthly Ads Budget" id="contact-budget" error={errors.monthly_budget}>
                      <select
                        id="contact-budget"
                        value={form.monthly_budget}
                        onChange={set("monthly_budget")}
                        className={inputClass}
                        data-testid="contact-budget-select"
                      >
                        <option value="">Select a range</option>
                        {BUDGETS.map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="City" id="contact-city" error={errors.city}>
                      <input
                        id="contact-city"
                        type="text"
                        value={form.city}
                        onChange={set("city")}
                        placeholder="e.g. Ghaziabad"
                        className={inputClass}
                        data-testid="contact-city-input"
                      />
                    </Field>
                    <Field label="Industry" id="contact-industry" error={errors.industry}>
                      <input
                        id="contact-industry"
                        type="text"
                        value={form.industry}
                        onChange={set("industry")}
                        placeholder="e.g. Real estate, Ed-tech"
                        className={inputClass}
                        data-testid="contact-industry-input"
                      />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Years in Business" id="contact-years" error={errors.years_in_business}>
                        <select
                          id="contact-years"
                          value={form.years_in_business}
                          onChange={set("years_in_business")}
                          className={inputClass}
                          data-testid="contact-years-select"
                        >
                          <option value="">Select an option</option>
                          {YEARS.map((y) => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                      </Field>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-button"
                    className="mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-brand-600 px-8 py-4 text-sm font-semibold text-white shadow-lg shadow-brand-600/20 transition-all duration-300 hover:bg-brand-700 active:scale-95 disabled:opacity-60"
                  >
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    Request My Free Strategy Call
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <aside className="h-full rounded-3xl bg-brand-950 p-8 sm:p-10 text-white" data-testid="contact-side-panel">
              <h2 className="font-display text-2xl font-bold">Talk to a human, not a bot.</h2>
              <p className="mt-3 text-sm leading-relaxed text-brand-100/70">
                Prefer to reach out directly? Here's where to find us.
              </p>
              <ul className="mt-8 space-y-6 text-sm">
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-brand-300">
                    <Mail className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-brand-300">Email</p>
                    <a href="mailto:chatbudds2@gmail.com" className="mt-1 block text-brand-50 transition-colors hover:text-white" data-testid="contact-email-link">
                      chatbudds2@gmail.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-brand-300">
                    <Phone className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-brand-300">Phone / WhatsApp</p>
                    <a href="tel:+918448444896" className="mt-1 block text-brand-50 transition-colors hover:text-white" data-testid="contact-phone-link">
                      +91 84484 44896
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-brand-300">
                    <MapPin className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-brand-300">Location</p>
                    <p className="mt-1 text-brand-50" data-testid="contact-address">
                      747, Niti Khand -1, Indirapuram, Ghaziabad
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-brand-300">
                    <Clock className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-brand-300">Response time</p>
                    <p className="mt-1 text-brand-50">Within 24 hours</p>
                  </div>
                </li>
              </ul>
            </aside>
          </Reveal>
        </div>
      </section>
    </>
  );
}
